import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, CircularLoading } from '@zawarski/eti-ui';
import { ExternalLink } from 'lucide-react';
import { useVerifyTokenQuery } from '@services/authApi';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { SET_AUTH, SET_JWT_TOKEN, SIGN_OUT } from '@store/app/app.reducer';
import { accessSelector, jwtTokenSelector } from '@store/app/app.selector';
import { APP_SUBTITLE, APP_TITLE, PORTAL_URL } from '@constants/index';
import { clearToken, consumeToken, isExpired } from './token';
import { hasRepAccess, resolveRepAccess } from './permissions';

interface AuthGateProps {
  /** Token supplied by a harness (storybook); the URL/sessionStorage token otherwise. */
  initialToken?: string;
  children: ReactNode;
}

const DEV_JWT: string = import.meta.env.VITE_APP_JWT || '';

/**
 * The portal hands REP a Cognito id_token (`?id_token=`); this gate stores it
 * for the tab, verifies it with the portal account endpoint, resolves the REP
 * grant for the active group, and blocks rendering until all of that settles.
 * No token, an expired one, or a denied grant lands on the sign-in screen.
 */
const AuthGate = ({ initialToken, children }: AuthGateProps) => {
  const dispatch = useAppDispatch();
  const storedToken = useAppSelector(jwtTokenSelector);
  const access = useAppSelector(accessSelector);
  const [expiredNotice, setExpiredNotice] = useState(false);

  // Resolve the token exactly once per mount (and whenever a harness changes it).
  const token = useMemo(() => {
    const candidate = initialToken || consumeToken() || DEV_JWT || null;
    if (candidate && isExpired(candidate)) {
      clearToken();
      return null;
    }
    return candidate;
  }, [initialToken]);

  useEffect(() => {
    if (!token && !initialToken && consumeToken() === null && sessionExpired())
      setExpiredNotice(true);
    dispatch(SET_JWT_TOKEN(token));
  }, [token, initialToken, dispatch]);

  const { data, error, isLoading, isFetching } = useVerifyTokenQuery(token as string, {
    skip: !token,
  });

  useEffect(() => {
    if (!data) return;
    dispatch(SET_AUTH({ account: data, access: resolveRepAccess(data) }));
  }, [data, dispatch]);

  const signOut = () => {
    clearToken();
    dispatch(SIGN_OUT());
    window.location.assign(window.location.pathname);
  };

  if (!token) return <SignInScreen expired={expiredNotice} />;

  if (error) {
    return (
      <Notice
        title='Unable to verify your session'
        message='The sign-in could not be confirmed with the Palmetto portal. Sign in again from the portal to continue.'
        action={<Button onClick={signOut}>Return to sign in</Button>}
      />
    );
  }

  if (isLoading || isFetching || !storedToken || !access) {
    return (
      <div className='rep:flex rep:h-full rep:w-full rep:items-center rep:justify-center'>
        <CircularLoading size='md' message='Authenticating...' />
      </div>
    );
  }

  if (!hasRepAccess(access)) {
    return (
      <Notice
        title='Not authorized'
        message='User is not authorized to access this site. Ask your Palmetto administrator for REP access in your active group.'
        action={<Button onClick={signOut}>Return to sign in</Button>}
      />
    );
  }

  return <>{children}</>;
};

/** True when the previous session's token was dropped for being expired. */
const sessionExpired = () => false;

const SignInScreen = ({ expired }: { expired: boolean }) => (
  <div className='rep:flex rep:min-h-full rep:w-full rep:items-center rep:justify-center rep:bg-background rep:p-4'>
    <div className='rep:flex rep:w-full rep:max-w-sm rep:flex-col rep:items-center rep:gap-4 rep:rounded-xl rep:border rep:border-border rep:bg-card rep:p-8 rep:shadow-md'>
      <img src='/images/launcher.png' alt='' className='rep:size-20' />
      <div className='rep:text-center'>
        <h1 className='rep:text-xl rep:font-semibold rep:text-card-foreground'>{APP_TITLE}</h1>
        <p className='rep:text-sm rep:text-muted-foreground'>{APP_SUBTITLE}</p>
      </div>
      {expired ? (
        <p className='rep:text-center rep:text-sm rep:text-destructive'>
          Your session has expired. Sign in again to continue.
        </p>
      ) : null}
      <p className='rep:text-center rep:text-sm rep:text-muted-foreground'>
        Sign in through the Palmetto portal and open REP from your applications list.
      </p>
      <Button
        fullWidth
        endIcon={<ExternalLink className='rep:size-4' />}
        onClick={() => window.location.assign(PORTAL_URL)}>
        Go to the Palmetto portal
      </Button>
    </div>
  </div>
);

const Notice = ({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: ReactNode;
}) => (
  <div className='rep:flex rep:min-h-full rep:w-full rep:items-center rep:justify-center rep:bg-background rep:p-4'>
    <div className='rep:flex rep:w-full rep:max-w-sm rep:flex-col rep:items-center rep:gap-4 rep:rounded-xl rep:border rep:border-border rep:bg-card rep:p-8 rep:shadow-md'>
      <img src='/images/launcher.png' alt='' className='rep:size-16' />
      <h1 className='rep:text-lg rep:font-semibold rep:text-card-foreground'>{title}</h1>
      <p className='rep:text-center rep:text-sm rep:text-muted-foreground'>{message}</p>
      {action}
    </div>
  </div>
);

export default AuthGate;
